<template>
  <section class="single-review" :class="{ desktop: $isDesktop }">
    <div class="review-header">
      <div class="rating-column" v-if="!$isDesktop">
        <client-only>
          <pu-modal
            :use-portal="true"
            :modal-hash="`single-review-${review.id}`"
          >
            <template #modal-trigger>
              <pu-rating-bubble
                :rating="review.rateOverall"
                :force-visibility="true"
                class="user-rating"
              />
              <span class="rating-meta">
                <translate>More</translate>
                <img
                  loading="lazy"
                  src="@/assets/images/info.svg"
                  width="18"
                  height="18"
                  alt=""
                />
              </span>
            </template>
            <div>
              <pu-rating-breakdown
                :breakdown="review"
                class="individual-review-breakdown"
              />
              <campsite-review-recommend
                :recommend="review.recommend"
                :revisit="review.revisit"
              />
              <p v-if="review.booked" class="verified">
                <img
                  loading="lazy"
                  src="@/assets/images/verified.svg"
                  class="confirmed"
                  alt=""
                  width="15"
                  height="15"
                /><translate>The reviewer booked through Pitchup.com</translate>
              </p>
            </div>
          </pu-modal>
        </client-only>
      </div>
      <h3 class="review-title" :data-g-translate="translateSelectorValue">
        {{ reviewTitle }}
      </h3>
    </div>

    <template v-if="$isDesktop">
      <pu-rating-breakdown
        :breakdown="review"
        class="individual-review-breakdown"
      />
    </template>

    <section class="reviewer">
      <div class="user-row">
        <user-avatar
          v-if="$isDesktop"
          :user="review.user"
          class="users-avatar"
        />
        <pu-date
          class="review-timestamp"
          :date="review.timestamp"
          :options="{ month: 'short', year: 'numeric' }"
        />
        <span class="user">
          <img
            v-if="!$isDesktop"
            loading="lazy"
            src="@/assets/images/avatar.svg"
            width="11"
            height="13"
            class="mobile-avatar"
            alt=""
          />
          <span v-if="review.user && !review.isAnonymous" class="user-name">{{
            review.user.username
          }}</span>
          <translate v-else class="user-name"> Anonymous </translate>
          <pu-flag
            v-if="review.country"
            class="users-country-flag"
            :title="countryTitle"
            :country-iso="review.country.iso"
          />
        </span>
      </div>

      <p class="pitch-name">
        <img
          loading="lazy"
          class="category-icon"
          :src="categoryIcon"
          :title="review.unitType.name"
          width="23"
          height="18"
          alt=""
        />
        <template v-if="review.pitchtype">
          {{ review.pitchtype.name }}
        </template>
      </p>
    </section>

    <section class="review-body">
      <p>
        <span class="prefix">
          <img
            loading="lazy"
            src="@/assets/images/tick-circle.svg"
            width="15"
            height="15"
            alt=""
          />
          <translate> Liked </translate>
        </span>
        <span
          :data-g-translate="translateSelectorValue"
          v-html="review.liked.replace(/(\r\n|\n|\r)/gm, '<br />')"
        />
      </p>
      <p v-if="review.disliked">
        <span class="prefix">
          <img
            loading="lazy"
            src="@/assets/images/cross-circle.svg"
            width="15"
            height="15"
            alt=""
          />
          <translate> Disliked </translate>
        </span>
        <span
          :data-g-translate="translateSelectorValue"
          v-html="review.disliked.replace(/(\r\n|\n|\r)/gm, '<br />')"
        />
      </p>
      <campsite-review-recommend
        v-if="$isDesktop"
        :recommend="review.recommend"
        :revisit="review.revisit"
      />
      <p v-if="review.comment" class="manager-comment">
        <translate class="prefix"> From the manager: </translate>
        <span
          :data-g-translate="translateSelectorValue"
          v-html="review.comment.replace(/(\r\n|\n|\r)/gm, '<br />')"
        />
      </p>
      <lazy-google-translate-button
        v-if="shouldShowTranslateButton"
        :query-selector-value="translateSelectorValue"
      />
    </section>
    <AsyncPitchTypePhotos
      v-if="review.photos.length"
      :key="getAsyncComponentKey('pitchTypePhotos')"
      :photos="review.photos"
      :use-lazy-images="true"
      class="review-photos"
      :fullscreen-modal-hash="`review-${review.id}-gallery`"
      @vue:mounted="setAsyncComponentKey('pitchTypePhotos')"
    />
  </section>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue'
import { hydrateOnVisibleSafe } from '../../../../utility/hydration/hydrateOnVisibleSafe'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

const glob = import.meta.glob('@/assets/images/category-icons/*.svg', {
  query: '?url',
  eager: true,
})

const AsyncPitchTypePhotos = defineAsyncComponent({
  loader: () => import('../../components/PitchTypePhotos.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

@Component({
  components: { AsyncPitchTypePhotos },
})
class CampsiteSingleReview extends Vue {
  @Prop()
  review

  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    if (this.$.isUnmounted) return
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  truncateWords(str: string, numberOfWords: number): string {
    return str.split(' ').splice(0, numberOfWords).join(' ')
  }

  get categoryIcon() {
    return glob[
      `/assets/images/category-icons/${this.review.unitType.slug}.svg`
    ]?.default
  }

  get countryTitle() {
    return this.$t("Reviewer's country: {countryName}", {
      countryName: this.review.country.name,
    })
  }

  get shouldShowTranslateButton() {
    return (
      this.review.language &&
      this.$i18n.locale.split('-')[0] !==
        this.review.language.code.split('-')[0]
    )
  }

  get translateSelectorValue() {
    return `review-${this.review.id}`
  }

  get reviewTitle(): string {
    const title =
      this.review.title || `${this.truncateWords(this.review.liked, 6)}…`
    return title.replace(/(\r\n|\n|\r)/gm, ' ')
  }
}

export default toNative(CampsiteSingleReview)
</script>

<style scoped>
.single-review {
  --body: 0.85em;
  background: #fff;
  border-radius: var(--border-radius);
  margin: 0 0.75em 1.25em;
  overflow: hidden;
  padding: 0.8em 0.8em 0.3em;
  width: calc(100% - 0.75em * 2);
  box-shadow:
    0 5px 25px rgba(0, 0, 0, 0.1),
    0 3px 10px rgba(0, 0, 0, 0.05);
}

.review-header {
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  min-height: 2.4em;
  position: relative;
  gap: 0.75em;
  margin-bottom: 0.4em;
}

.rating-bubble.user-rating {
  font-size: 1.3em;
}

.rating-bubble.user-rating:before {
  content: '';
}

.rating-column {
  position: relative;
  margin: 0 0 0 0.35em;
  flex-shrink: 0;
}

.rating-meta {
  display: block;
}

.rating-meta span {
  font-size: 12px;
  color: #999;
}

.rating-meta img {
  filter: hue-rotate(-5deg) brightness(1.2);
}

.review-title {
  font-weight: bold;
  font-size: 0.95em;
  line-height: 1.2;
  margin: 0 0 0.3em 0;
  color: var(--colour-dark-blue);
}

.reviewer {
  padding: 0.25em 0 0.4em;
  border-top: 1px solid var(--colour-table-borders);
  border-bottom: 1px solid var(--colour-table-borders);
}

.user-row {
  font-size: var(--body);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0 0 0.3em 0;
  gap: 0.5em;
  color: var(--colour-text-light);
}

.user {
  display: flex;
  align-items: center;
}

.user .mobile-avatar {
  margin-right: 0.2em;
  margin-top: -1px;
}

.user-name {
  margin: 0 0.3em 0 0;
}

.users-country-flag {
  display: inline-block;
  width: 1.5em;
  height: auto;
  margin: 0;
}

.pitch-name {
  font-size: var(--body);
  margin: 0;
  color: var(--colour-text-light);
  display: flex;
  gap: 0.3em;
  line-height: 1.1;
}

.pitch-name :deep(.category-icon) {
  height: 1.1em;
}

.category-icon {
  width: auto;
  height: 1.25em;
  vertical-align: bottom;
}

.review-body {
  margin-top: 0.65em;
}

.review-body p {
  font-size: var(--body);
  margin: 0 0 1em 0;
  line-height: 1.35;
}

.review-body .prefix {
  font-weight: bold;
  display: flex;
  gap: 0.4em;
}

.manager-comment {
  padding: 0 0 0 1.3em;
  position: relative;
}

.manager-comment:before {
  content: '';
  display: block;
  width: 0.45em;
  height: calc(100% - 6px);
  background: var(--colour-page-background-light);
  border-radius: 1em;
  position: absolute;
  left: 0;
  top: 3px;
  bottom: auto;
}

.review-photos {
  margin: 1em 0 0;
}

.review-photos :deep(.photo-wrapper) {
  max-width: calc(100% / 3);
  min-height: auto;
  border: none;
}

.review-photos :deep(.photo-wrapper img) {
  border-radius: var(--border-radius);
}

.review-photos :deep(.photos-row) {
  justify-content: start;
  background: white;
  cursor: zoom-in;
  gap: 0.3em;
}

.review-photos :deep(.flickity-enabled.is-fullscreen) {
  left: 0vw;
  top: 0vw;
}

.review-helpful {
  margin: 1.5em -1.4em 0;
  padding-top: 1em;
  border-top: 0.4em dotted #eef6fa;
  font-size: var(--body);
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.user-recommends) {
  margin: 0 1.4em 1em;
  font-size: var(--body);
}

.verified {
  display: flex;
  align-items: center;
  gap: 0.35em;
  margin: 1.5em 1.4em 1.4em;
  font-size: var(--small);
}

.confirmed {
  width: 1.25em;
  height: 1.25em;
}

/* desktop & tablet */
.single-review.desktop {
  --body: inherit;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: auto auto auto;
  align-items: start;
  grid-column-gap: 3em;
  grid-row-gap: 0;
  border-radius: 0;
  margin: 0;
  width: 100%;
  box-shadow: none;
  border-top: 2px solid var(--colour-page-background);
  padding: 1em;
  position: relative;
}

.desktop .review-header {
  grid-column: 2/4;
  grid-row: 1/2;
  min-height: 0;
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  margin: 0;
}

.desktop .reviewer {
  grid-column: 1/2;
  grid-row: 1/3;
  padding: 0;
  border: none;
}

.desktop .user-row {
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: auto auto;
  grid-column-gap: 0.5em;
  grid-row-gap: 0;
  margin-bottom: 1em;
}

.desktop .reviewer :deep(.users-avatar) {
  flex-shrink: 0;
  width: 2.2em;
  height: 2.2em;
  margin: 0;
  display: block;
  grid-row: 1/3;
  grid-column: 1/2;
  z-index: unset;
}

.desktop .user {
  grid-row: 1/2;
  grid-column: 2/3;
}

.desktop .user-name {
  font-weight: bold;
  font-size: initial;
  margin: 0 0.3em 0 0;
  white-space: nowrap;
}

.desktop .review-timestamp {
  grid-row: 2/3;
  grid-column: 2/3;
}

.desktop .review-title {
  font-size: 1.25em;
  color: var(--colour-dark-blue);
}

.desktop .review-body {
  grid-column: 2/4;
  grid-row: 2/3;
}

.single-review.desktop :deep(.individual-review-breakdown) {
  grid-row: 1/4;
  grid-column: 4/5;
  margin: 0;
}

.desktop .review-photos {
  margin: 1em 0;
  grid-row: 3/4;
  grid-column: 2/4;
}

.desktop .review-photos :deep(.photo-wrapper img:not(.icon)) {
  width: 10em;
}

.desktop .review-photos :deep(.photo-wrapper) {
  min-height: auto;
  border-width: 2px;
}

.desktop .review-photos :deep(.photos-row) {
  min-height: auto;
  justify-content: flex-start;
}

.desktop .review-photos :deep(.photo-count-fullscreen) {
  padding: 0 0.7em 0.5em;
}

.desktop .review-body p,
.desktop .review-helpful {
  font-size: inherit;
  line-height: 1.4;
}
</style>
